'use client';

import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { RiArrowLeftLine, RiMailLine, RiMailSendLine, RiRefreshLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import {
  AuthBrand,
  AuthField,
  FieldError,
  FormAlert,
  SubmitButton,
  fieldV,
} from '@/components/auth-parts';
import { forgotPassword } from '@/lib/session';
import { ApiError } from '@/lib/api';
import { Link } from '@/i18n/navigation';
import { dur, ease, sceneContainer, sceneItem, spring, springFade } from '@/lib/motion';
import { siteLinks } from '@/lib/site-links';

/**
 * Şifre sıfırlama isteği — tek sütun, koyu panel YOK.
 *
 * ── Neden bölünmüş tuval değil ────────────────────────────────────────────
 * Giriş ve kayıt birer varış noktası; bu ekran bir ara durak. Kullanıcı buraya
 * bir şey okumaya değil, on saniyelik bir işi bitirip girişe dönmeye geliyor.
 * Yanına ürün paneli koymak "burada da vakit geçir" demek olurdu. Sayfa sessiz:
 * tek sütun, ortada, arkada yalnızca çok hafif bir ızgara dokusu.
 *
 * ── İki durum ─────────────────────────────────────────────────────────────
 * 1. İstek — tek alan, tek buton.
 * 2. Gönderildi — adresin teyidi, süre bilgisi, tekrar gönder ve adres değiştir.
 *
 * Geçiş `mode="wait"`: iki panel asla üst üste binmiyor, biri tamamen çıkmadan
 * diğeri girmiyor. İkisi aynı anda görünseydi kullanıcı hangisinin geçerli
 * olduğunu bilemezdi. Sarmalayıcıda `layout="position"` var — panelin boyu
 * değişince ortalanmış blok ZIPLAMIYOR, kayıyor (DESIGN.md §1: ölçü değil
 * transform).
 *
 * Durum değişince odak yeni panelin başlığına taşınıyor. Görsel kullanıcı için
 * değişim apaçık; klavye ve ekran okuyucu kullanıcısı için odak hâlâ kaybolmuş
 * butonun yerinde kalırdı ve yeni metni ancak baştan gezinerek bulurdu.
 *
 * ── Backend ───────────────────────────────────────────────────────────────
 * E-posta servisi henüz bağlı değil (CLAUDE.md → Durum). Gönderim gerçek bir
 * istek atmıyor; ikinci panel bu yüzden alt satırında bunu AÇIKÇA söylüyor
 * (`sent.demo`). Bu satır, uç bağlanana kadar kalır — kullanıcıya gitmemiş bir
 * e-postayı beklettirmek, ekranın kendisinden daha büyük bir hata olurdu.
 */

/** Kabaca "@ var, nokta var, boşluk yok". Asıl doğrulama sunucuda. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Tekrar gönder kilidi. Gerçek uçta sunucu da aynı süreyi uygulamalı —
    buradaki sayaç yalnızca arayüz tarafı. */
const RESEND_SECONDS = 45;

const panelV = {
  hidden: { opacity: 0, y: 10, filter: 'blur(4px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  exit: { opacity: 0, y: -8, filter: 'blur(4px)', transition: { duration: dur.instant, ease: ease.in } },
};

export function ForgotForm() {
  const t = useTranslations('forgot');
  const uid = useId();

  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  const [tried, setTried] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const emailRef = useRef<HTMLInputElement>(null);
  const sentHeadingRef = useRef<HTMLHeadingElement>(null);
  /* İlk açılışta odağı hiçbir yere taşımıyoruz — sayfa yeni yüklendiğinde
     odağı çalmak, kullanıcının nereye geldiğini duymasını engelliyor. Yalnızca
     DURUM DEĞİŞTİĞİNDE taşınıyor; bu bayrak ikisini ayırıyor. */
  const swapped = useRef(false);

  /* Sayaç yalnızca gönderildi panelinde çalışıyor; panel kapanınca temizleniyor
     (aksi hâlde arka planda saymaya devam edip bir sonraki gönderimde yanlış
     yerden başlıyordu). */
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (!swapped.current) return;
    if (sentTo) sentHeadingRef.current?.focus();
    else emailRef.current?.focus();
  }, [sentTo]);

  function onChange(value: string) {
    setEmail(value);
    if (tried) setError(EMAIL.test(value.trim()) ? undefined : t('email.error'));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);

    if (!EMAIL.test(email.trim())) {
      setError(t('email.error'));
      emailRef.current?.focus();
      return;
    }

    setError(undefined);
    setFormError(undefined);
    setPending(true);

    try {
      await forgotPassword(email.trim().toLowerCase());
    } catch (error) {
      // Hız sınırı dışındaki her sonuç "gönderildi" gibi görünüyor: sunucu
      // kayıtlı olmayan adres için de 204 dönüyor (adres taraması olmasın
      // diye) ve arayüz o dili bozmamalı.
      if (error instanceof ApiError && error.status === 429) {
        setPending(false);
        setFormError(t('tooMany'));
        return;
      }
    }

    setPending(false);
    swapped.current = true;
    setSentTo(email.trim());
    setCountdown(RESEND_SECONDS);
  }

  function backToForm() {
    swapped.current = true;
    setSentTo(null);
    setCountdown(0);
  }

  const emailId = `${uid}-email`;

  return (
    <section className="auth-col">
      <motion.div
        className="auth-form-inner auth-form-inner--narrow"
        variants={sceneContainer}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={sceneItem}>
          <AuthBrand />
        </motion.div>

        {/* `initial={false}`: ilk boyamada içerideki panel KENDİ girişini
            oynatmıyor — sahneye girişi zaten dışarıdaki `fieldV` yapıyor. İkisi
            birden oynasaydı panel iki kez, iki farklı hızda belirirdi. */}
        <motion.div className="auth-swap" variants={fieldV} layout="position">
          <AnimatePresence mode="wait" initial={false}>
            {sentTo === null ? (
              <motion.div
                key="request"
                variants={panelV}
                initial="hidden"
                animate="show"
                exit="exit"
              >
                <div className="auth-head">
                  <h1 className="auth-title">{t('title')}</h1>
                  <p className="auth-lead">{t('lead')}</p>
                </div>

                <form className="auth-form" onSubmit={onSubmit} noValidate>
                  <div className="auth-field">
                    <label className="auth-label" htmlFor={emailId}>
                      {t('email.label')}
                    </label>
                    <AuthField
                      invalid={Boolean(error)}
                      icon={<RiMailLine size={18} aria-hidden="true" />}
                    >
                      <input
                        ref={emailRef}
                        id={emailId}
                        className="auth-input"
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        placeholder={t('email.placeholder')}
                        value={email}
                        onChange={(e) => onChange(e.target.value)}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? `${emailId}-error` : undefined}
                      />
                    </AuthField>
                    <FieldError id={`${emailId}-error`} message={error} />
                  </div>

                  <FormAlert message={formError} />

                  <SubmitButton
                    pending={pending}
                    label={t('submit')}
                    pendingLabel={t('submitting')}
                  />
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="sent"
                variants={panelV}
                initial="hidden"
                animate="show"
                exit="exit"
              >
                {/* İkon kutu içinde değil, doğrudan zemine oturuyor
                    (DESIGN.md §3). Yay `bouncy` DEĞİL: bu bir kutlama anı değil,
                    bir teyit. Zıplayan bir ikon "başardın!" der; oysa kullanıcı
                    henüz şifresini değiştirmedi, işin yarısında. */}
                <motion.span
                  className="auth-sent-icon"
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={springFade(spring.default, dur.fast)}
                  aria-hidden="true"
                >
                  <RiMailSendLine size={26} />
                </motion.span>

                <div className="auth-head">
                  {/* tabIndex={-1}: fareyle odaklanamaz, kodla odaklanabilir.
                      Durum değişince odak buraya geliyor. */}
                  <h1 className="auth-title" ref={sentHeadingRef} tabIndex={-1}>
                    {t('sent.title')}
                  </h1>
                  <p className="auth-lead">
                    {t.rich('sent.lead', {
                      address: sentTo,
                      email: (chunks) => <strong className="auth-sent-address">{chunks}</strong>,
                    })}
                  </p>
                </div>

                <p className="auth-sent-hint">{t('sent.hint')}</p>

                <div className="auth-sent-actions">
                  {/* Sayaç bitmeden buton pasif — arka arkaya beş kez tıklanan
                      "tekrar gönder" beş e-posta demek. Kalan süre METİNDE
                      yazıyor: pasiflik tek başına neden pasif olduğunu
                      söylemiyor. */}
                  <motion.button
                    type="button"
                    className="auth-ghost"
                    onClick={() => setCountdown(RESEND_SECONDS)}
                    disabled={countdown > 0}
                    whileHover={countdown > 0 ? undefined : { scale: 1.01, y: -1 }}
                    whileTap={countdown > 0 ? undefined : { scale: 0.99 }}
                    transition={spring.snap}
                  >
                    <RiRefreshLine size={16} aria-hidden="true" />
                    {countdown > 0 ? t('sent.resendIn', { seconds: countdown }) : t('sent.resend')}
                  </motion.button>

                  <button type="button" className="auth-text-button" onClick={backToForm}>
                    {t('sent.change')}
                  </button>
                </div>

                {/* Bu satır ekranın dürüstlüğü: e-posta servisi bağlanana kadar
                    kalır (bileşen başlığındaki nota bak). */}
                <p className="auth-demo-note">{t('sent.demo')}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Girişe dönüş her iki durumda da aynı yerde duruyor — panel değişince
            yer değiştirseydi kullanıcı çıkışı yeniden aramak zorunda kalırdı. */}
        <motion.p className="auth-alt" variants={sceneItem} layout="position">
          <Link href={siteLinks.login.href} className="auth-alt-link auth-alt-link--back">
            <RiArrowLeftLine size={16} aria-hidden="true" />
            {t('backToLogin')}
          </Link>
        </motion.p>

        <motion.p className="auth-legal" variants={sceneItem} layout="position">
          {t.rich('noAccount', {
            register: (chunks) => (
              <Link href={siteLinks.register.href} className="auth-legal-link">
                {chunks}
              </Link>
            ),
          })}
        </motion.p>
      </motion.div>
    </section>
  );
}
